import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import MediaPicker, { resolveMediaUrl } from "@/components/admin/MediaPicker";


const EMPTY = {
  name: "",
  name_en: "",
  capacity: 1,
  price: 0,
  currency: "ريال/شهر",
  currency_en: "SAR/month",
  show_price: true,
  available: true,
  reserved_until: "",
  image: "",
  images: [],
  description: "",
  description_en: "",
  order: 0,
  active: true,
};


/* ─────────────────────────────────────────
   Lightbox
───────────────────────────────────────── */
function Lightbox({ images, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);


  useEffect(() => { setIdx(startIndex); }, [startIndex]);


  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length, onClose]);


  return (
    <div
      className="fixed inset-0 z-[999] bg-black/90 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      {/* زر الإغلاق */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
      >
        <X size={20} />
      </button>


      {/* عداد */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-bold">
        {idx + 1} / {images.length}
      </div>


      {/* الصورة */}
      <div
        className="relative max-w-5xl max-h-[85vh] w-full mx-6"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[idx]}
          alt={`${idx + 1}`}
          className="w-full h-full object-contain max-h-[85vh] rounded-lg"
        />
      </div>


      {/* أسهم */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}


      {/* Dots */}
      {images.length > 1 && (
        <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setIdx(i); }}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-white" : "w-2 bg-white/40 hover:bg-white/60"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────
   معرض الصور داخل الكارد
───────────────────────────────────────── */
function CardGallery({ images, onLightbox }) {
  const [idx, setIdx] = useState(0);
  if (!images || images.length === 0) return null;


  return (
    <div className="relative w-full bg-white/5 group">
      {/* الصورة - بدون قص */}
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: "16/9" }}>
        <img
          src={images[idx]}
          alt={`${idx + 1}`}
          className="w-full h-full object-contain bg-black/30"
        />
        {/* زر التكبير */}
        <button
          onClick={() => onLightbox(idx)}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ZoomIn size={14} />
        </button>
        {/* عداد */}
        {images.length > 1 && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-bold">
            {images.length} صور
          </div>
        )}
      </div>


      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-1.5 p-2 overflow-x-auto bg-black/20">
          {images.map((src, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-all ${
                i === idx ? "border-[#f47424]" : "border-transparent opacity-60 hover:opacity-90"
              }`}
            >
              <img src={src} alt={`thumb-${i}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────
   الصفحة الرئيسية
───────────────────────────────────────── */
export default function Offices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, images: [], startIndex: 0 });


  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/offices");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);


  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...EMPTY, ...it, images: it.images || [], reserved_until: it.reserved_until || "" });
    setOpen(true);
  };


  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        capacity: Number(form.capacity),
        price: Number(form.price),
        order: Number(form.order),
        reserved_until: form.reserved_until || null,
        images: Array.isArray(form.images)
          ? form.images
          : (typeof form.images === "string"
            ? form.images.split("\n").map((s) => s.trim()).filter(Boolean)
            : []),
      };
      if (editing) await api.put(`/admin/offices/${editing.id}`, body);
      else await api.post("/admin/offices", body);
      toast.success("تم الحفظ");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };


  const remove = async (id) => {
    if (!window.confirm("حذف هذا المكتب؟")) return;
    try {
      await api.delete(`/admin/offices/${id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };


  const openLightbox = (office, startIndex = 0) => {
    const imgs = [
      ...(office.image ? [resolveMediaUrl(office.image)] : []),
      ...(office.images || []).map(resolveMediaUrl),
    ].filter(Boolean);
    setLightbox({ open: true, images: imgs, startIndex });
  };


  return (
    <div data-testid="admin-offices-page">
      {/* Lightbox */}
      {lightbox.open && (
        <Lightbox
          images={lightbox.images}
          startIndex={lightbox.startIndex}
          onClose={() => setLightbox({ ...lightbox, open: false })}
        />
      )}


      <div className="mb-6 flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">المكاتب الخاصة</h1>
          <p className="text-white/50 text-sm mt-1">إدارة المكاتب الخاصة المعروضة على الموقع</p>
        </div>
        <Button onClick={openNew} className="bg-[#f47424] hover:bg-[#f47424]/90" data-testid="office-add-btn">
          <Plus className="w-4 h-4 ml-1" /> إضافة مكتب
        </Button>
      </div>


      {loading ? (
        <div className="text-center py-10 text-white/40">
          <Loader2 className="w-5 h-5 animate-spin inline" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => {
            const allImgs = [
              ...(it.image ? [resolveMediaUrl(it.image)] : []),
              ...(it.images || []).map(resolveMediaUrl),
            ].filter(Boolean);


            return (
              <div
                key={it.id}
                className="bg-white/[0.03] border border-white/[0.06] rounded-2xl overflow-hidden"
                data-testid={`office-card-${it.id}`}
              >
                {/* ✅ معرض الصور */}
                {allImgs.length > 0 && (
                  <CardGallery
                    images={allImgs}
                    onLightbox={(i) => openLightbox(it, i)}
                  />
                )}


                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="font-bold">{it.name}</div>
                      <div className="text-xs text-white/50">{it.name_en}</div>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <Badge
                        variant="outline"
                        className={it.available
                          ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                          : "bg-rose-500/15 text-rose-300 border-rose-500/30"}
                      >
                        {it.available ? "متاح" : "محجوز"}
                      </Badge>
                      {!it.active && (
                        <Badge variant="outline" className="bg-white/10 text-white/50 border-white/10">
                          غير نشط
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-white/70 mb-3">
                    <div>السعة: {it.capacity} أشخاص</div>
                    <div>السعر: {it.price} {it.currency}</div>
                    {it.reserved_until && (
                      <div className="text-xs text-amber-300">محجوز حتى: {it.reserved_until}</div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(it)}
                      className="flex-1 border-white/10 bg-transparent text-white hover:bg-white/5"
                    >
                      <Pencil className="w-3.5 h-3.5 ml-1" /> تعديل
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => remove(it.id)}
                      className="text-white/50 hover:text-rose-400 hover:bg-rose-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}


      {/* ─── Dialog التعديل/الإضافة ─── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="bg-[#0F2537] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-right">{editing ? "تعديل مكتب" : "مكتب جديد"}</DialogTitle>
          </DialogHeader>


          {/* Arabic */}
          <section className="bg-white/[0.02] border border-white/10 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold bg-[#f47424]/20 text-[#f47424]">AR</span>
              <h3 className="text-sm font-bold text-white">المحتوى العربي</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="الاسم" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label="العملة/الوحدة" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} />
              <div className="md:col-span-2">
                <Label className="text-white/70">الوصف</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/[0.04] border-white/10 text-white mt-1" />
              </div>
            </div>
          </section>


          {/* English */}
          <section className="bg-white/[0.02] border border-white/10 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold bg-[#f47424]/20 text-[#f47424]">EN</span>
              <h3 className="text-sm font-bold text-white">English content</h3>
            </div>
            <div dir="ltr" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" value={form.name_en} onChange={(v) => setForm({ ...form, name_en: v })} />
              <Field label="Currency / unit" value={form.currency_en} onChange={(v) => setForm({ ...form, currency_en: v })} />
              <div className="md:col-span-2">
                <Label className="text-white/70">Description</Label>
                <Textarea rows={3} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="bg-white/[0.04] border-white/10 text-white mt-1" />
              </div>
            </div>
          </section>


          {/* Shared */}
          <section className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
            <h3 className="text-sm font-bold text-white mb-3">إعدادات مشتركة</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="السعة" type="number" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} />
              <Field label="السعر" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
              <Field label="الترتيب" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
              <Field label="محجوز حتى (YYYY-MM-DD)" value={form.reserved_until || ""} onChange={(v) => setForm({ ...form, reserved_until: v })} />
              <div className="md:col-span-2">
                <Label className="text-white/70">الصورة الرئيسية</Label>
                <div className="mt-2">
                  <MediaPicker value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="اختيار الصورة" />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-white/70">صور إضافية</Label>
                <div className="mt-2">
                  <MediaPicker value={Array.isArray(form.images) ? form.images : []} onChange={(v) => setForm({ ...form, images: v })} multiple label="أضف صور" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.show_price !== false} onCheckedChange={(v) => setForm({ ...form, show_price: v })} />
                <Label className="text-white/80">إظهار السعر للزائر</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} />
                <Label className="text-white/80">متاح للحجز</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <Label className="text-white/80">نشط (معروض)</Label>
              </div>
            </div>
          </section>


          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10 bg-transparent text-white">
              إلغاء
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#f47424] hover:bg-[#f47424]/90" data-testid="office-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <Label className="text-white/70">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white/[0.04] border-white/10 text-white mt-1"
      />
    </div>
  );
}
